import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { paymentAPI, ordersAPI } from '../utils/api';

export const CheckoutPage = () => {
  const { cart, cartTotal } = useCart();
  const { user } = useAuth(); // Need user details for pre-filling Razorpay
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: '+91',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Helper to load Razorpay Script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!cart.length) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // 1. Load Razorpay Script
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        toast.error('Razorpay SDK failed to load. Are you online?');
        setLoading(false);
        return;
      }

      // 2. Create Order on Backend
      const orderData = await paymentAPI.createRazorpayOrder();

      // 3. Configure Razorpay Options
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Replace with your Key ID (or load from env)
        amount: orderData.amount,
        currency: orderData.currency,
        name: "BuggyBerry",
        description: "Transaction for Order",
        image: "https://via.placeholder.com/150", // Add your logo URL here
        order_id: orderData.id,

        // 4. Handler for Success
        handler: async function (response) {
          try {
            // 1. Verify Payment Signature
            const verifyData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

            const verification = await paymentAPI.verifyPayment(verifyData);

            if (verification.success) {
              toast.success('Payment Verified. Creating Order...');

              // --- UPDATED PAYLOAD ---
              const orderPayload = {
                shipping_address: formData,
                payment_id: response.razorpay_payment_id // <--- PASS THIS ID
              };

              const newOrder = await ordersAPI.create(orderPayload);
              navigate(`/order-confirmation/${newOrder.id}`);

            } else {
              toast.error('Payment verification failed.');
            }
          } catch (error) {
            console.error("Order Creation Error:", error);
            toast.error('Payment successful but order creation failed. Contact Support.');
          }
        },
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          address: `${formData.address}, ${formData.city}`,
        },
        theme: {
          color: "#F4E06D", // Matching your yellow theme
        },
        // 5. Modal Closed Handler (User canceled)
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast("Payment cancelled. You can try again.");
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (error) {
      console.error(error);
      toast.error('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-outfit font-semibold text-2xl mb-4">Your cart is empty</h2>
          <Button onClick={() => navigate('/products')} className="rounded-full">
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-outfit font-semibold text-3xl md:text-4xl mb-8">Checkout</h1>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Shipping Form */}
          <div>
            <h2 className="font-semibold text-xl mb-4">Shipping Information</h2>
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="zipCode">Zip Code *</Label>
                  <Input
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full py-6 bg-primary text-primary-foreground hover:bg-primary/90 font-bold mt-6"
              >
                {loading ? 'Processing...' : `Pay Now (₹${cartTotal.toFixed(2)})`}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <h2 className="font-semibold text-xl mb-4">Order Summary</h2>
            <div className="bg-white rounded-lg border border-border p-6 space-y-4">
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {cart.map((item) => {
                  const price = item.product.price;
                  return (
                    <div key={item.id} className="flex gap-3">
                      <img
                        src={item.product.images[0]}
                        alt={item.product.name}
                        className="w-16 h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.product.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.size} | {item.color}
                        </p>
                        <p className="text-sm font-mono">
                          ₹ {price.toFixed(2)} x {item.quantity}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2 pt-4 border-t border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹ {cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>Free</span>
                </div>
              </div>

              <div className="flex justify-between text-lg font-bold pt-4 border-t border-border">
                <span>Total</span>
                <span>₹ {cartTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};